import type { EvidenceUploadMode } from 'api';
import { createContext, type ReactNode, useState } from 'react';
import { createStore, type StoreApi } from 'zustand';

import type { Bale } from '../../types';

interface BaleStore {
  bales: Bale[];
  mode: EvidenceUploadMode;
  parentId: string;
  actions: {
    addBale: (bale: Bale) => void;
    removeBale: (baleId: string) => void;
    updateBale: (bale: Bale) => void;
    setBales: (bales: Bale[]) => void;
    updateParentId: (newParentId: string) => void;
  };
}

const BaleAnalysisContext = createContext<StoreApi<BaleStore> | null>(null);

const BaleAnalysisProvider = ({
  children,
  mode,
  parentId,
}: {
  children: ReactNode;
  mode: EvidenceUploadMode;
  parentId: string;
}) => {
  const [store] = useState(() =>
    createStore<BaleStore>((set) => ({
      actions: {
        addBale: (newBale: Bale) =>
          set((state) => ({
            bales: [...state.bales, newBale],
          })),
        removeBale: (baleId: string) =>
          set((state) => {
            return {
              bales: state.bales.filter((bale) => bale.id !== baleId),
            };
          }),
        setBales: (bales: Bale[]) =>
          set({
            bales,
          }),
        updateBale: (updatedBale: Bale) =>
          set((state) => ({
            bales: state.bales.map((bale) => (bale.id === updatedBale.id ? { ...bale, ...updatedBale } : bale)),
          })),
        updateParentId: (newParentId: string) =>
          set({
            parentId: newParentId,
          }),
      },
      bales: [],
      mode,
      parentId,
    }))
  );

  return <BaleAnalysisContext.Provider value={store}>{children}</BaleAnalysisContext.Provider>;
};

export { BaleAnalysisContext, BaleAnalysisProvider, type BaleStore };
